// src/components/BangkokPage.jsx
// Bangkok event landing page — June 2026 one-off event
// Single conversion goal: reserve a spot ($10 via Stripe)
// Route: /bangkok

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  XMarkIcon,
  UsersIcon,
  MapPinIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  ArrowRightIcon,
  ClockIcon,
  CameraIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import SEO from './SEO';

// ── UPDATE before the event ────────────────────────────────────────────────────
const EVENT = {
  date: 'Saturday, 27 June 2026',
  time: '10:30 AM – 12:30 PM',
  city: 'Bangkok',
  priceUSD: '$10',
  priceTHB: '≈ 300 THB',
  spotsLeft: 10,
};

// test mode stripe: https://buy.stripe.com/test_aFafZifct4LAcv73Xsew800
const STRIPE_LINK = 'https://buy.stripe.com/test_aFafZifct4LAcv73Xsew800';
// live mode

// const STRIPE_LINK = 'https://buy.stripe.com/fZu00kd4lb9Y2Ux65Aew801';
// ──────────────────────────────────────────────────────────────────────────────

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
});

const testimonials = [
  {
    quote: "Three months ago I was completely isolated. ConnectCo changed everything — from feeling lost to making genuine friends. This community gave me a sense of home.",
    name: "Sarah L.",
    flag: "🇺🇸",
    role: "Digital nomad",
  },
  {
    quote: "At first I felt self-conscious about showing up. But now it's become a treasured routine. I look forward to meeting new people — and to seeing familiar faces.",
    name: "Terry",
    flag: "🇺🇸🇯🇵",
    role: "Founding member",
  },
  {
    quote: "Moving countries is harder than anyone tells you. Finding this community was the thing that made it feel possible.",
    name: "Pedro N.",
    flag: "🇺🇸",
    role: "Member",
  },
];

const notThis = [
  "Not a networking event — no business cards, no pitches",
  "Not speed dating — no romantic pressure whatsoever",
  "Not a Facebook group — this happens in person, in a room",
  "Not a conference — no panels, no speakers, no agenda",
  "Not a large crowd — intentionally small and unhurried",
];

const communityLinks = [
  {
    icon: CameraIcon,
    label: "Community photos",
    sublabel: "See who shows up",
    href: "https://connectco.app/photos",
  },
  {
    icon: CalendarDaysIcon,
    label: "Tokyo & Osaka events",
    sublabel: "Our regular gatherings",
    href: "https://connectco.app/events",
  },
  {
    icon: GlobeAltIcon,
    label: "About ConnectCo",
    sublabel: "Who we are and why",
    href: "https://connectco.app/about",
  },
  {
    icon: SparklesIcon,
    label: "What is fika?",
    sublabel: "The full story",
    href: "https://connectco.app/what-is-fika",
  },
];

export const bangkokSeoConfig = {
  title: "Fika in Bangkok — June 2026 | Connect & Co",
  description:
    "A Swedish fika gathering coming to Bangkok this June. 10 people. One morning. No networking, no agenda. Just the kind of honest conversation that turns strangers into friends. $10 event pass.",
  keywords:
    "fika bangkok, expat events bangkok, digital nomad bangkok, social events bangkok, meet people bangkok, international community bangkok, swedish fika bangkok, connectco bangkok",
  url: "https://connectco.app/bangkok",
  canonical: "https://connectco.app/bangkok",
  openGraph: {
    title: "Fika is Coming to Bangkok — June 2026",
    description:
      "Connect & Co brings the Swedish fika tradition to Bangkok for one morning this June. 10 people. No networking. No agenda. $10.",
    url: "https://connectco.app/bangkok",
    image: "https://connectco.app/og-bangkok.jpg",
    type: "website",
    site_name: "ConnectCo",
    locale: "en_US",
  },
  twitter: {
    card: "summary_large_image",
    title: "Fika is Coming to Bangkok — June 2026",
    description:
      "10 people. One morning. No networking, no agenda. Just honest conversation. $10 event pass.",
    image: "https://connectco.app/og-bangkok.jpg",
  },
};

const BangkokPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleStripeClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'bangkok_reserve_click', {
        event_category: 'conversion',
        event_label: 'bangkok_stripe_cta',
        value: 9,
      });
    }
  };

  return (
    <>
      <SEO config={bangkokSeoConfig} />

      <div className="min-h-screen bg-stone-950 text-stone-100">

        {/* ── HERO ──────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden pt-28 pb-20 px-6">

          {/* Ambient glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-700/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 max-w-3xl mx-auto text-center">

            <motion.div {...fadeUp(0)}>
              <span className="inline-flex items-center gap-2 bg-amber-500/15 text-amber-400 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-6">
                <SparklesIcon className="w-3.5 h-3.5" />
                One morning · Bangkok · June 2026
              </span>
            </motion.div>

            <motion.h1 {...fadeUp(0.1)} className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6">
              Fika is coming
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
                to Bangkok.
              </span>
            </motion.h1>

            <motion.p {...fadeUp(0.2)} className="text-lg sm:text-xl text-stone-300 leading-relaxed mb-4 max-w-2xl mx-auto">
              A Swedish coffee ritual for people tired of networking events.
              No agenda. No pitches. No name badges.
              Just 10 people in a room, slowing down long enough to actually talk.
            </motion.p>

            <motion.p {...fadeUp(0.25)} className="text-stone-400 text-sm mb-10">
              ConnectCo's first Bangkok gathering — one morning only.
            </motion.p>

            {/* Event details strip */}
            <motion.div {...fadeUp(0.3)} className="flex flex-wrap justify-center gap-3 mb-10 text-sm">
              <span className="flex items-center gap-1.5 bg-stone-800 px-4 py-2 rounded-full text-stone-300">
                <CalendarDaysIcon className="w-4 h-4 text-amber-400" />
                {EVENT.date}
              </span>
              <span className="flex items-center gap-1.5 bg-stone-800 px-4 py-2 rounded-full text-stone-300">
                <ClockIcon className="w-4 h-4 text-amber-400" />
                {EVENT.time}
              </span>
              <span className="flex items-center gap-1.5 bg-stone-800 px-4 py-2 rounded-full text-stone-300">
                <UsersIcon className="w-4 h-4 text-amber-400" />
                {EVENT.spotsLeft} spots only
              </span>
              <span className="flex items-center gap-1.5 bg-amber-500/20 border border-amber-500/30 px-4 py-2 rounded-full text-amber-300 font-semibold">
                <CurrencyDollarIcon className="w-4 h-4" />
                {EVENT.priceUSD} event pass
              </span>
            </motion.div>

            {/* Primary CTA */}
            <motion.div {...fadeUp(0.35)}>
              <a
                href={STRIPE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleStripeClick}
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-stone-900 font-bold text-lg px-10 py-4 rounded-full hover:from-amber-400 hover:to-orange-400 hover:scale-105 transition-all duration-300 shadow-lg shadow-amber-500/20"
              >
                Reserve My Spot — {EVENT.priceUSD}
                <ArrowRightIcon className="w-5 h-5" />
              </a>
              <p className="text-stone-500 text-xs mt-4">
                {EVENT.priceTHB} · {EVENT.spotsLeft} spots · everyone pays their own drinks · secure checkout via Stripe
              </p>
            </motion.div>

          </div>
        </section>

        {/* ── WHAT IS FIKA ──────────────────────────────────────────── */}
        <section className="py-20 px-6 bg-stone-900">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-center">
                What is{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
                  fika?
                </span>
              </h2>
              <p className="text-stone-300 text-lg leading-relaxed mb-6 text-center">
                Fika (pronounced <em>fee-ka</em>) is a Swedish social tradition — a deliberate pause for honest conversation
                without agendas, status games, or business cards.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 mt-10">
                {[
                  { emoji: '🇸🇪', title: 'Swedish origin', body: "A national ritual. Swedes do it twice a day. The point is never the coffee — it's the person across from you." },
                  { emoji: '🤝', title: 'Always social', body: "You never fika alone. It's about making space for whatever emerges naturally — no pressure, no performance." },
                  { emoji: '☕', title: 'Bangkok edition', body: 'Great coffee, a room full of interesting people, and permission to just be somewhere for a while.' },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="bg-stone-800 rounded-2xl p-6 text-center"
                  >
                    <div className="text-3xl mb-3">{item.emoji}</div>
                    <h3 className="font-semibold text-stone-100 mb-2">{item.title}</h3>
                    <p className="text-stone-400 text-sm leading-relaxed">{item.body}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── THE MORNING ───────────────────────────────────────────── */}
        <section className="py-20 px-6">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">What actually happens</h2>
              <p className="text-stone-400 text-lg">A Saturday morning in a Bangkok specialty café. That's it.</p>
            </motion.div>

            <div className="space-y-4">
              {[
                { icon: UsersIcon, title: '10 people maximum', body: 'Small enough that everyone gets to actually talk to each other. Not a crowd — a room.' },
                { icon: ChatBubbleLeftRightIcon, title: 'No agenda, no structure', body: 'No icebreaker games, no roundtable introductions, no name badges. Just conversation, the way it works when it actually works.' },
                { icon: MapPinIcon, title: 'Bangkok specialty coffee', body: 'Held at a third-wave café in Bangkok. The kind of place where the coffee is taken seriously. Everyone orders and pays for their own.' },
                { icon: CalendarDaysIcon, title: 'Saturday morning, 10 AM – 12:30 PM', body: 'Long enough to settle into a real conversation. Short enough to leave your afternoon free.' },
                { icon: SparklesIcon, title: 'Organised by ConnectCo', body: "We've been running fika gatherings in Tokyo and Osaka since 2025. This is our first time in Bangkok." },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-stone-900 rounded-2xl p-5 border border-stone-800"
                >
                  <div className="w-10 h-10 bg-amber-500/15 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <item.icon className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-100 mb-1">{item.title}</h3>
                    <p className="text-stone-400 text-sm leading-relaxed">{item.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHO THIS IS FOR ───────────────────────────────────────── */}
        <section className="py-20 px-6 bg-stone-900">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Who comes to fika</h2>
              <p className="text-stone-400 text-lg mb-10">
                Expats. Digital nomads. Locals who think internationally.
                People who moved to Bangkok knowing almost no one.
                People who've been here years and still feel like they're missing something real.
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { emoji: '✈️', label: 'Expats & long-termers', desc: 'Settled in Bangkok but still building a real social circle.' },
                  { emoji: '💻', label: 'Digital nomads', desc: 'In Bangkok for weeks or months. Want connection, not another coworking small-talk session.' },
                  { emoji: '🌏', label: 'Globally-minded locals', desc: 'Bangkok residents who are internationally curious and comfortable in English.' },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="bg-stone-800 rounded-2xl p-6"
                  >
                    <div className="text-3xl mb-3">{item.emoji}</div>
                    <h3 className="font-semibold text-stone-100 mb-2">{item.label}</h3>
                    <p className="text-stone-400 text-sm leading-relaxed">{item.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── WHAT THIS IS NOT ──────────────────────────────────────── */}
        <section className="py-20 px-6">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">What this is not</h2>
              <div className="space-y-3">
                {notThis.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.07 }}
                    className="flex items-center gap-3 bg-stone-900 border border-stone-800 rounded-xl px-5 py-4"
                  >
                    <XMarkIcon className="w-5 h-5 text-rose-400 flex-shrink-0" />
                    <span className="text-stone-300 text-sm">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── TESTIMONIALS ──────────────────────────────────────────── */}
        <section className="py-20 px-6 bg-stone-900">
          <div className="max-w-3xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl font-bold mb-10 text-center"
            >
              From the Tokyo community
            </motion.h2>
            <div className="space-y-4">
              {testimonials.map((t, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-stone-800 rounded-2xl p-6 border border-stone-700"
                >
                  <p className="text-stone-300 leading-relaxed mb-4 italic">"{t.quote}"</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{t.flag}</span>
                    <div>
                      <span className="text-stone-100 font-semibold text-sm">{t.name}</span>
                      <span className="text-stone-500 text-sm"> · {t.role}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SEE THE COMMUNITY ─────────────────────────────────────── */}
        <section className="py-20 px-6">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-10"
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">We're not new to this</h2>
              <p className="text-stone-400">
                ConnectCo has been running in Tokyo and Osaka since 2025 — 450+ members, real events, real people.
              </p>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-4">
              {communityLinks.map((link, i) => (
                <motion.a
                  key={i}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-center gap-4 bg-stone-900 border border-stone-800 rounded-2xl px-5 py-4 hover:border-amber-500/40 hover:bg-stone-800 transition-all duration-200 group"
                >
                  <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/20 transition-colors">
                    <link.icon className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-stone-100 font-medium text-sm">{link.label}</p>
                    <p className="text-stone-500 text-xs">{link.sublabel}</p>
                  </div>
                  <ArrowRightIcon className="w-4 h-4 text-stone-600 group-hover:text-amber-400 transition-colors flex-shrink-0" />
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* ── PRESS ─────────────────────────────────────────────────── */}
        <section className="py-12 px-6 border-y border-stone-800">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-stone-500 text-xs uppercase tracking-widest mb-2">As seen in</p>
            <p className="text-stone-300 text-sm">
              <a
                href="https://metropolisjapan.com/fika-in-tokyo-swedish-coffee-ritual/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-stone-100 underline underline-offset-2 hover:text-amber-400 transition-colors"
              >
                Metropolis Japan
              </a>
              {' '}— "How a Swedish Coffee Ritual Is Helping Professionals Feel Less Alone" (Feb 2026)
            </p>
          </div>
        </section>

        {/* ── FINAL CTA ─────────────────────────────────────────────── */}
        <section className="py-24 px-6">
          <div className="max-w-xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-4xl mb-4">☕</div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                10 spots. One morning.
              </h2>
              <p className="text-stone-400 text-lg mb-2">
                {EVENT.date} · {EVENT.time}
              </p>
              <p className="text-stone-500 text-sm mb-8">
                Bangkok · {EVENT.priceUSD} ({EVENT.priceTHB}) · everyone pays their own drinks
              </p>

              <a
                href={STRIPE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleStripeClick}
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-stone-900 font-bold text-lg px-10 py-4 rounded-full hover:from-amber-400 hover:to-orange-400 hover:scale-105 transition-all duration-300 shadow-lg shadow-amber-500/20"
              >
                Reserve My Spot — {EVENT.priceUSD}
                <ArrowRightIcon className="w-5 h-5" />
              </a>

              <p className="text-stone-600 text-xs mt-8">
                Questions?{' '}
                <a href="mailto:hello@connectco.app" className="text-stone-400 hover:text-stone-200 underline transition-colors">
                  hello@connectco.app
                </a>
                {' '}·{' '}
                <a href="https://connectco.app" className="text-stone-400 hover:text-stone-200 underline transition-colors">
                  connectco.app
                </a>
              </p>
            </motion.div>
          </div>
        </section>

      </div>
    </>
  );
};

export default BangkokPage;
